import { createPath, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { LoginCallback } from '@okta/okta-react';
import { useCallback, useEffect, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { Dashboard } from './Pages/Dashboard';
import { CustomerPage } from './Pages/Customer';
import { Processing } from './Pages/Processing';
import { ComponentSpinner } from './Compounds/Loading/ComponentSpinner';
import {
  getUserLoadingSubject,
  getUserSubject,
  User,
  UserRole,
  hasAnalystOrHigherPermissions,
  hasDataOrHigherPermissions,
  hasEngineerOrHigherPermissions
} from '../store/User';
import { CustomerDetail } from './Pages/Customer/CustomerDetail';
import { AssetPage } from './Pages/Asset';
import { LinkPage } from './Pages/Link';
import { RunPage } from './Pages/Run/RunPage';
import { ProvisionalAssetPage } from './Pages/ProvisionalAsset';
import { RPMAccountDetailPage } from './Pages/RPM/RPMAccountDetailPage';
import { AssetDetailPage } from './Pages/Asset/AssetDetailPage';
import { LinkDetailPage } from './Pages/Link/LinkDetailPage';
import { BulkMergePage } from './Pages/BulkMerge';
import { RPMAccountPage } from './Pages/RPM';
import { useAuth } from '../Context/AuthContext';
import { EntraLoginCallback } from './Compounds/LoginCallback/EntraLoginCallback';
import { TicketPage } from './Pages/Ticket';
import { TicketDetailPage } from './Pages/Ticket/TicketDetailPage';
import { TFUPage } from './Pages/TFU';
import { ProvisionalAssetDetailPage } from './Pages/ProvisionalAsset/ProvisionalAssetDetailPage';
import { ErrorPage } from './Pages/Error';
import { UserTab } from './Pages/Admin/Tabs/User/UserTab';
import { CalculationsTab } from './Pages/Admin/Tabs/Calculations/CalculationsTab';
import { LogTab } from './Pages/Admin/Tabs/Log/LogTab';
import { RunTotalAuditTab } from './Pages/Admin/Tabs/RunTotalAudit/RunTotalAuditTab';
import { HistoricalCharts } from './Pages/Admin/Tabs/RunTotalAudit/HistoricalCharts';
import { SupplierMapTab } from './Pages/Data/Tabs/SupplierMap/SupplierMapTab';
import { TsdMapTab } from './Pages/Data/Tabs/TsdMap/TsdMapTab';
import { ApMapTab } from './Pages/Data/Tabs/ApMap/ApMapTab';
import { ProductMap } from './Pages/Data/Tabs/ProductMap';
import { SyncTab } from './Pages/Data/Tabs/RPMSync/SyncTab';
import { InvalidLinkTab } from './Pages/Data/Tabs/InvalidLink/InvalidLinkTab';
import { CreateProvisionalAssetTab } from './Pages/Data/Tabs/CreateProvisionalAsset/CreateProvisionalAssetTab';
import { BulkArchiveLinks } from './Compounds/Linking/BulkArchiveLinks/BulkArchiveLinks';
import { CustomerMap } from './Pages/Data/Tabs/CustomerMap';
import { BulkCreationByAssetFields } from './Compounds/Linking/BulkCreationByAssetFields/BulkCreationByAssetFields';
import { PaymentFrequencyExceptions } from './Pages/Data/Tabs/PaymentFrequency/PaymentFrequencyExceptions';
import { SearchPage } from './Pages/Search/SearchPage';
import { PaymentPage } from './Pages/Payment';
import { PaymentRecipientsPage } from './Pages/Payment/PaymentRecipientsPage';
import { CorrectionPage } from './Pages/Correction';
import { AdjustmentPage } from './Pages/Adjustment';
import { InstanceTab } from './Pages/Admin/Tabs/Instance/InstanceTab';
import { AllocationBreakdownPage } from './Pages/RPM/AllocationBreakdownPage';
import { AssetRevenueAttributionPage } from './Pages/Attribution/AssetRevenue';
import { AttributionDirectSellerPage } from './Pages/Attribution/DirectSeller';
import { PageErrorBoundary } from './PageErrorBoundary';
import { HistoryPage } from './Pages/History';
import { SettingsPage } from './Pages/Settings';
import { getSignInUnavailableSubject, setSignInUnavailable } from '../store/Auth';

export const ProtectedRoute = () => {
  const { authState, signIn, token } = useAuth();
  const location = useLocation();
  const [userLoading, setUserLoading] = useState(false);
  const [user, setUser] = useState<User>();
  const [signInFailed, setSignInFailed] = useState(false);

  useEffect(() => {
    const usrLoadingSubscription = getUserLoadingSubject().subscribe((l) => setUserLoading(l));
    const userSub = getUserSubject().subscribe((u) => setUser(u));
    const signInSub = getSignInUnavailableSubject().subscribe((f) => setSignInFailed(f));

    return () => {
      if (usrLoadingSubscription) usrLoadingSubscription.unsubscribe();
      if (userSub) userSub.unsubscribe();
      if (signInSub) signInSub.unsubscribe();
    };
  }, []);

  const redirectToSignIn = useCallback(() => {
    setSignInUnavailable(false);
    // The redirect is a promise: unhandled, a failed one leaves the user on the spinner with no way out.
    signIn(createPath(location)).catch((error) => {
      datadogLogs.logger.error('Sign-in redirect failed', { error });
      setSignInUnavailable(true);
    });
  }, [signIn, location]);

  useEffect(() => {
    if (authState && !authState.isPending && !authState.isAuthenticated && !authState.error) {
      redirectToSignIn();
    }
  }, [authState, redirectToSignIn]);

  // Auth can succeed elsewhere (another tab, a silent renew) after a redirect rejected, so a stale
  // failure must never outrank a live session.
  if ((signInFailed || authState?.error) && !authState?.isAuthenticated) {
    return (
      <div
        data-testid="sign-in-error"
        role="alert"
        className="flex flex-col items-center justify-center min-h-[40vh] p-8 text-center">
        <h2 className="text-xl font-semibold text-grey-9 mb-2">Can&apos;t reach sign-in</h2>
        <p className="text-sm text-grey-6 mb-6 max-w-md">
          We couldn&apos;t connect to the sign-in service. Check your connection and try again.
        </p>
        <button
          type="button"
          onClick={redirectToSignIn}
          className="px-4 py-2 rounded-md bg-indigo text-sm text-white hover:bg-indigo-mid">
          Try again
        </button>
      </div>
    );
  }

  if (authState?.isPending || !authState?.isAuthenticated || userLoading || !token || !user) {
    return (
      <div className="flex h-[70vh]">
        <ComponentSpinner
          sx={{ mt: 'auto' }}
          label="Loading..."
        />
      </div>
    );
  } else if (user?.role === UserRole.INACTIVE) {
    return (
      <div
        data-testid="inactive-message"
        className="flex items-center justify-center min-h-screen text-xl font-semibold">
        The user is inactive and cannot perform this action
      </div>
    );
  }

  return (
    <PageErrorBoundary>
      <Outlet />
    </PageErrorBoundary>
  );
};

export default function AppRoutes() {
  const { provider } = useAuth();
  const [user, setUser] = useState<User>();
  useEffect(() => {
    const userSub = getUserSubject().subscribe((u) => setUser(u));

    return () => {
      if (userSub) userSub.unsubscribe();
    };
  }, []);

  return (
    <Routes>
      <Route
        path="login/callback"
        element={provider === 'entra' ? <EntraLoginCallback /> : <LoginCallback />}
      />
      <Route element={<ProtectedRoute />}>
        <Route
          path="/"
          element={<Dashboard />}
        />
        <Route
          path={'/*'}
          element={<Dashboard />}
        />
        <Route
          path="/customer"
          element={<CustomerPage />}
        />
        <Route
          path={'/customer/:id'}
          element={<CustomerDetail />}
        />
        <Route
          path={'/ticket'}
          element={<TicketPage />}
        />
        <Route
          path={'/ticket/:id'}
          element={<TicketDetailPage />}
        />
        <Route
          path={'/ticket-follow-ups'}
          element={<TFUPage />}
        />
        <Route
          path={'/rpm-account'}
          element={<RPMAccountPage />}
        />
        <Route
          path={'/rpm-account/:id'}
          element={<RPMAccountDetailPage />}
        />
        <Route
          path="/rpm-account/:id/allocation-breakdown"
          element={<AllocationBreakdownPage />}
        />
        <Route
          path="/rpm-account/:id/allocation-breakdown/:monthYear"
          element={<AllocationBreakdownPage />}
        />
        <Route
          path="/processing"
          element={<Processing />}
        />
        <Route
          path="/asset"
          element={<AssetPage />}
        />
        <Route
          path="/asset/:id"
          element={<AssetDetailPage />}
        />
        <Route
          path="/history"
          element={<HistoryPage />}
        />
        <Route
          path="/runs"
          element={<RunPage />}
        />
        <Route
          path="/link"
          element={<LinkPage />}
        />
        <Route
          path="/link/:id"
          element={<LinkDetailPage />}
        />
        <Route
          path="/provisional-asset"
          element={<ProvisionalAssetPage />}
        />
        <Route
          path="/error/:code/:object/:objectId/:timestamp"
          element={<ErrorPage />}
        />
        <Route
          path="/provisional-asset/:id"
          element={<ProvisionalAssetDetailPage />}
        />
        <Route
          path="/search"
          element={<SearchPage />}
        />
        {hasEngineerOrHigherPermissions() && (
          <Route
            path="/settings"
            key="settings-page"
            element={<SettingsPage />}
          />
        )}
        {hasAnalystOrHigherPermissions() && [
          <Route
            path="/asset-revenue-attribution"
            key="asset-revenue-attribution-page"
            element={<AssetRevenueAttributionPage />}
          />,
          <Route
            path="/downloads"
            key="downloads-redirect"
            element={
              <Navigate
                to="/asset-revenue-attribution"
                replace
              />
            }
          />
        ]}
        {hasDataOrHigherPermissions() && [
          <Route
            path="/payment"
            key="payment-page"
            element={<PaymentPage />}
          />,
          <Route
            path="/payment/recipients"
            key="payment-recipients-page"
            element={<PaymentRecipientsPage />}
          />
        ]}
        <Route
          path="/correction"
          key="correction-page"
          element={<CorrectionPage />}
        />
        <Route
          path="/adjustment"
          key="adjustment-page"
          element={<AdjustmentPage />}
        />
        <Route
          path="/attribution/direct-seller"
          key="attribution-direct-seller-page"
          element={<AttributionDirectSellerPage />}
        />
        {[UserRole.ADMIN, UserRole.ENGINEER].includes(user?.role as UserRole) && (
          <>
            <Route
              path="/admin/users"
              element={<UserTab />}
            />
            <Route
              path="/admin/fields"
              element={<CalculationsTab />}
            />
            <Route
              path="/admin/log"
              element={<LogTab />}
            />
            <Route
              path="/admin/run-total-audits"
              element={<RunTotalAuditTab />}
            />
            <Route
              path="/admin/run-total-audits/historical-charts"
              element={<HistoricalCharts />}
            />

            <Route
              path="/link/bulk-archive"
              element={<BulkArchiveLinks />}
            />
            <Route
              path="/link/bulk-creation-by-asset-fields"
              element={<BulkCreationByAssetFields />}
            />
            <Route
              path="/admin/instance"
              element={<InstanceTab />}
            />
          </>
        )}
        {[UserRole.ADMIN, UserRole.ENGINEER, UserRole.DATA].includes(user?.role as UserRole) && (
          <>
            <Route
              path="/data/supplier-map"
              element={<SupplierMapTab />}
            />
            <Route
              path="/data/customer-map"
              element={<CustomerMap />}
            />
            <Route
              path="/data/tsd-map"
              element={<TsdMapTab />}
            />
            <Route
              path="/data/ap-map"
              element={<ApMapTab />}
            />
            <Route
              path="/data/product-map"
              element={<ProductMap />}
            />
            <Route
              path="/data/rpm-sync"
              element={<SyncTab />}
            />
            <Route
              path="/data/rpm-cleanup"
              element={<InvalidLinkTab />}
            />
            <Route
              path="/bulk-merge"
              element={<BulkMergePage />}
            />
            <Route
              path="/data/create-provisional-asset"
              element={<CreateProvisionalAssetTab />}
            />
            <Route
              path="/data/payment-frequency-exceptions"
              element={<PaymentFrequencyExceptions />}
            />
          </>
        )}
      </Route>
    </Routes>
  );
}
